import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MotifButton,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
} from "@ey-xd/motif-react";
import { actionIcDescription24px } from "@ey-xd/motif-react/assets/icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SuccessMessage from "@/pages/CaseDetail/Distributions/ManualDataSource/SuccessMessage/SuccessMessage";
import ErrorMessage from "@/pages/CaseDetail/Distributions/ManualDataSource/ErrorMessage/ErrorMessage";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import { fileExtensions } from "@/services/documents/documentsReducer";
import { useBulkImportDocumentMutation } from "@/services/import/bulkImportReducer";
import { ActiveTabsOrder } from "@/menuData";
interface uploadProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  title: string;
  uploadLabel: string;
  uploadDescription: string;
  maxFiles: number;
  onClose: () => void;
  onSucessRefreshApi?: () => void;
}

const BankActivitiesUploader = ({
  showModal,
  setShowModal,
  title,
  uploadLabel,
  uploadDescription,
  maxFiles,
  onClose,
  onSucessRefreshApi,
}: uploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { caseId } = useParams<{ caseId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [uploadDocument] = useBulkImportDocumentMutation();
  const [discardDetails, setDiscardDetails] = useState(false);

  function handleDiscardReject() {
    setDiscardDetails(false);
  }
  function handleDiscardApprove() {
    setDiscardDetails(false);
    handleClose();
  }
  const handleFileChange = (files: any) => {
    setFile(files[0]);
  };

  const handleClose = () => {
    if (uploadStatus === "Success") {
      onSucessRefreshApi && onSucessRefreshApi();
    }
    onClose();
  };
  const handleSubmit = async () => {
    setIsUploading(true);
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("DisplayName", file.name.replace(/\.[^/.]+$/, ""));
      formData.append("Entitytype", "9");
      if (caseId) {
        formData.append("CaseId", caseId);
      }
      try {
        await uploadDocument(formData).unwrap();
        setUploadStatus("Success");
      } catch (error: any) {
        setUploadStatus("error");
        setErrorMessage(error?.data?.message || undefined);
      } finally {
        setIsUploading(false);
      }
    }
  };
  return (
    <>
      <MotifModal
        onClose={() => {
          if (
            file &&
            (!uploadStatus ||
              (uploadStatus !== "Success" && uploadStatus !== "error"))
          ) {
            setDiscardDetails(true);
          } else {
            handleClose();
          }
        }}
        show={showModal}
        size="lg"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <MotifModalHeader
          closeButtonProps={{
            "aria-label": "Custom Close Button aria-label",
            title: "Custom Close Button title",
          }}
        >
          <span>{title}</span>
        </MotifModalHeader>
        {uploadStatus === "Success" ? (
          <SuccessMessage />
        ) : uploadStatus === "error" ? (
          <ErrorMessage errorMessage={errorMessage} />
        ) : (
          <MotifModalBody className="pad-t-10">
            <div className="pad-v-20">
              <MotifFileUploader
                id="bulk-upload"
                label={uploadLabel}
                labelDescription={uploadDescription}
                onDrop={handleFileChange}
                accept={{ ...fileExtensions.csv, ...fileExtensions.txt }}
                maxFiles={maxFiles}
              >
                {file && (
                  <MotifFileUploaderItem
                    fileIcon={<MotifIcon src={actionIcDescription24px} />}
                    fileName={file.name}
                    uploaded={true}
                    uploadedText={
                      t("pages.claims.documentPopup.displayName") + file.name
                    }
                    error={
                      (file && file.size >= fileExtensions.zipMaxFileSize) ||
                      false
                    }
                    errorText={t(
                      "pages.claims.stageStatusPopup.filesizeExceedsLimit",
                      {
                        filesize: "250",
                      }
                    )}
                  />
                )}
              </MotifFileUploader>
            </div>
          </MotifModalBody>
        )}
        <MotifModalFooter>
          <div className="document-footer display-flex ">
            <MotifButton
              type="button"
              variant="secondary"
              className="cancelBtn mar-r-10"
              onClick={() => {
                if (file && !uploadStatus) {
                  setDiscardDetails(true);
                } else {
                  handleClose();
                }
              }}
            >
              {t("global.globalActions.cancel")}
            </MotifButton>
            {uploadStatus === "Success" || uploadStatus === "error" ? (
              <MotifButton
                onClick={() => {
                  navigate(
                    `/case/${caseId}?activeTab=${ActiveTabsOrder.IMPORT}`
                  );
                }}
                variant="primary"
                className="cancelBtn"
              >
                {t("pages.paymentStatus.importSummaryLabel")}
              </MotifButton>
            ) : (
              <MotifButton
                onClick={handleSubmit}
                variant="primary"
                className="cancelBtn"
                disabled={
                  isUploading ||
                  !file ||
                  (file && file.size >= fileExtensions.zipMaxFileSize) ||
                  false
                }
              >
                {isUploading
                  ? t("global.globalActions.uploading")
                  : t("global.globalActions.upload")}
              </MotifButton>
            )}
          </div>
        </MotifModalFooter>
      </MotifModal>
      <ConfirmationModal
        isVisible={discardDetails}
        setIsVisible={handleDiscardReject}
        cancelBtnApproveHandler={handleDiscardApprove}
        cancelBtnRejectHandler={handleDiscardReject}
      />
    </>
  );
};

export default BankActivitiesUploader;
